import * as SliderPrimitive from "@radix-ui/react-slider";
import type * as React from "react";

import { cn } from "@krea/shared/utils";

interface ISliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  trackClassName?: string;
  /**
   * Props for the thumb, which is the element Radix gives `role="slider"` —
   * so accessibility props belong here, NOT on the root. Use it to name the
   * control (`aria-label`) and, when the underlying value is an index into a
   * list rather than the number the user sees, to announce the real value
   * (`aria-valuetext`); otherwise a screen reader reads out the bare index.
   */
  thumbProps?: React.ComponentPropsWithoutRef<typeof SliderPrimitive.Thumb>;
}

const Slider = ({
  className,
  trackClassName,
  thumbProps,
  ref,
  ...props
}: ISliderProps & {
  ref?: React.Ref<React.ElementRef<typeof SliderPrimitive.Root>>;
}) => (
  <SliderPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex w-full touch-none select-none items-center",
      "[&>span:has([role=slider])]:z-10",
      className,
    )}
    {...props}
  >
    <SliderPrimitive.Track
      className={cn(
        "group relative z-0 h-[14px] w-full min-w-0 grow cursor-pointer overflow-hidden rounded-full",
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 rounded-full bg-white"
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 z-1 rounded-full bg-primary-150 transition-colors duration-300 ease-in-out group-hover:bg-primary/10 group-active:bg-primary/10",
          trackClassName,
        )}
      />
      <SliderPrimitive.Range className="absolute z-2 h-full min-w-0 bg-primary" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb
      {...thumbProps}
      className={cn(
        "block box-border h-8 w-8 cursor-grab rounded-full border-solid border-12 border-primary bg-white ring-offset-white transition-colors focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
        thumbProps?.className,
      )}
    />
  </SliderPrimitive.Root>
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
